import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import BackButton from './BackButton';
import { LoadingPage } from './LoadingStates';

interface User {
  id: number;
  name: string;
  email: string;
  branch?: string;
  department?: string;
}

interface RevenueEntry {
  user_id: number;
  month: string;
  gross_revenue: number;
  pay_amount?: number;
}

const RevenueManualEntry: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form state
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [grossRevenue, setGrossRevenue] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [revPayRatio, setRevPayRatio] = useState<number | null>(null);

  // Filters
  const [branchFilter, setBranchFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Recent entries
  const [recentEntries, setRecentEntries] = useState<any[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchRecentEntries();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      // Filter to only show non-admin users
      const filteredUsers = response.data.filter((user: any) => !user.is_admin);
      setUsers(filteredUsers);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentEntries = async () => {
    try {
      setLoadingEntries(true);
      const response = await api.get('/revenue/recent-entries?limit=10');
      setRecentEntries(response.data);
    } catch (err: any) {
      console.error('Failed to fetch recent entries:', err);
      // Don't show error for recent entries as it's not critical
    } finally {
      setLoadingEntries(false);
    }
  };

  // Calculate rev/pay ratio when values change
  useEffect(() => {
    const revenue = parseFloat(grossRevenue) || 0;
    const pay = parseFloat(payAmount) || 0;
    
    if (pay > 0 && revenue > 0) {
      setRevPayRatio(revenue / pay);
    } else {
      setRevPayRatio(null);
    }
  }, [grossRevenue, payAmount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedUser || !month || !grossRevenue) {
      setError('Please fill in all required fields');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const data: RevenueEntry = {
        user_id: selectedUser,
        month,
        gross_revenue: parseFloat(grossRevenue),
      };

      if (payAmount) {
        data.pay_amount = parseFloat(payAmount);
      }

      await api.post('/revenue/manual-entry', data);
      
      setSuccess('Revenue data saved successfully!');
      
      // Reset form
      setSelectedUser(null);
      setGrossRevenue('');
      setPayAmount('');
      setRevPayRatio(null);
      
      // Refresh recent entries
      fetchRecentEntries();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save revenue data');
    } finally {
      setSaving(false);
    }
  };

  // Filter users based on filters and search
  const filteredUsers = users.filter(user => {
    if (branchFilter !== 'all' && user.branch !== branchFilter) return false;
    if (departmentFilter !== 'all' && user.department !== departmentFilter) return false;
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const name = user.name.toLowerCase();
      const email = user.email.toLowerCase();
      return name.includes(search) || email.includes(search);
    }
    
    return true;
  });

  // Get unique branches and departments for filters
  const branches = Array.from(new Set(users.map(u => u.branch).filter(Boolean)));
  const departments = Array.from(new Set(users.map(u => u.department).filter(Boolean)));

  if (loading) return <LoadingPage message="Loading users..." />;

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-4xl mx-auto p-6">
        <BackButton />
        
        <h1 className="text-3xl font-bold mb-8">Manual Revenue Entry</h1>

        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-900/50 border border-green-500 text-green-200 p-4 rounded mb-4">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg p-6 space-y-6">
          {/* User Selection Section */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Select Technician</h2>
            
            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Branch</label>
                <select
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-500"
                >
                  <option value="all">All Branches</option>
                  {branches.map(branch => (
                    <option key={branch} value={branch}>{branch}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Department</label>
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-500"
                >
                  <option value="all">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Search</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Type to filter users..."
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-500"
                />
                {searchTerm && (
                  <p className="text-xs text-gray-400 mt-1">
                    Showing {filteredUsers.length} of {users.length} users
                  </p>
                )}
              </div>
            </div>

            {/* User Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Technician <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedUser || ''}
                onChange={(e) => setSelectedUser(Number(e.target.value))}
                required
                className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-500"
              >
                <option value="">Select a technician...</option>
                {filteredUsers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name} - {user.email}
                    {user.branch && ` (${user.branch}`}
                    {user.department && ` - ${user.department}`}
                    {user.branch && ')'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Revenue Data Section */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Revenue Data</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Month <span className="text-red-500">*</span>
                </label>
                <input
                  type="month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  required
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Gross Revenue <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    value={grossRevenue}
                    onChange={(e) => setGrossRevenue(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                    className="w-full bg-gray-700 text-white pl-8 pr-3 py-2 rounded border border-gray-600 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Pay Amount <span className="text-gray-400">(Optional)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full bg-gray-700 text-white pl-8 pr-3 py-2 rounded border border-gray-600 focus:border-blue-500"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Pay data entry will be required starting January 2025
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Rev/Pay Ratio</label>
                <div className="bg-gray-700 px-3 py-2 rounded border border-gray-600">
                  {revPayRatio !== null ? (
                    <span className={`font-semibold ${
                      revPayRatio >= 35 ? 'text-green-400' :
                      revPayRatio >= 30 ? 'text-yellow-400' :
                      revPayRatio >= 25 ? 'text-orange-400' :
                      'text-red-400'
                    }`}>
                      {revPayRatio.toFixed(2)}:1
                    </span>
                  ) : (
                    <span className="text-gray-500">N/A</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin-dashboard')}
              className="px-6 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Revenue Data'}
            </button>
          </div>
        </form>

        {/* Quick Entry History */}
        <div className="mt-8 bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Recent Entries</h2>
          {loadingEntries ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400 mx-auto"></div>
              <p className="text-gray-400 mt-2">Loading recent entries...</p>
            </div>
          ) : recentEntries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-600">
                    <th className="text-left py-2 px-3 text-gray-300">Date</th>
                    <th className="text-left py-2 px-3 text-gray-300">Technician</th>
                    <th className="text-left py-2 px-3 text-gray-300">Month</th>
                    <th className="text-right py-2 px-3 text-gray-300">Revenue</th>
                    <th className="text-right py-2 px-3 text-gray-300">Pay</th>
                    <th className="text-right py-2 px-3 text-gray-300">Ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {recentEntries.map((entry: any, index: number) => (
                    <tr key={index} className="border-b border-gray-700 hover:bg-gray-700/50">
                      <td className="py-2 px-3 text-gray-300">
                        {new Date(entry.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-2 px-3 text-white">
                        {entry.user?.name || 'Unknown User'}
                      </td>
                      <td className="py-2 px-3 text-gray-300">
                        {new Date(entry.month).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short' 
                        })}
                      </td>
                      <td className="py-2 px-3 text-right text-white">
                        ${(entry.gross_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 px-3 text-right text-white">
                        {entry.pay_amount ? `$${entry.pay_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : 'N/A'}
                      </td>
                      <td className="py-2 px-3 text-right">
                        {entry.pay_amount && entry.pay_amount > 0 ? (
                          <span className={`font-semibold ${
                            (entry.gross_revenue / entry.pay_amount) >= 35 ? 'text-green-400' :
                            (entry.gross_revenue / entry.pay_amount) >= 30 ? 'text-yellow-400' :
                            (entry.gross_revenue / entry.pay_amount) >= 25 ? 'text-orange-400' :
                            'text-red-400'
                          }`}>
                            {(entry.gross_revenue / entry.pay_amount).toFixed(2)}:1
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-400">
              No recent entries found. Revenue data will appear here after saving.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueManualEntry;
